import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from './ui/dialog';
import { Badge } from './ui/badge';
import { IndianRupee, Wallet, Clock, CheckCircle, XCircle, AlertCircle } from 'lucide-react';
import { toast } from '../hooks/use-toast';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const WithdrawalRequestForm = ({ userId, currentEarnings, onSuccess }) => {
  const [minAmount, setMinAmount] = useState(1000);
  const [withdrawalRequests, setWithdrawalRequests] = useState([]);
  const [loading, setLoading] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [formData, setFormData] = useState({
    amount: '',
    upi_id: ''
  });

  useEffect(() => {
    fetchMinAmount();
    fetchWithdrawalRequests();
  }, []);

  const fetchMinAmount = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/admin/settings`);
      setMinAmount(response.data.min_withdrawal_amount || 1000);
    } catch (error) {
      console.error('Failed to fetch settings');
    }
  };

  const fetchWithdrawalRequests = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/withdrawal/requests/${userId}`);
      setWithdrawalRequests(response.data);
    } catch (error) {
      console.error('Failed to fetch withdrawal requests');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    const amount = parseFloat(formData.amount);
    
    if (amount < minAmount) {
      toast({
        title: "Invalid Amount",
        description: `Minimum withdrawal amount is ₹${minAmount}`,
        variant: "destructive"
      });
      return;
    }

    if (amount > currentEarnings) {
      toast({
        title: "Insufficient Balance",
        description: `You only have ₹${currentEarnings} available`,
        variant: "destructive"
      });
      return;
    }

    if (!formData.upi_id) {
      toast({
        title: "UPI ID Required",
        description: "Please enter your UPI ID",
        variant: "destructive"
      });
      return;
    }

    setLoading(true);

    try {
      await axios.post(
        `${BACKEND_URL}/api/withdrawal/request?user_id=${userId}`,
        {
          amount: amount,
          upi_id: formData.upi_id
        }
      );
      
      toast({
        title: "Success",
        description: "Withdrawal request submitted successfully"
      });
      
      setDialogOpen(false);
      setFormData({ amount: '', upi_id: '' });
      fetchWithdrawalRequests();
      
      if (onSuccess) {
        onSuccess();
      }
    } catch (error) {
      toast({
        title: "Error",
        description: error.response?.data?.detail || "Failed to submit withdrawal request",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  const hasPendingRequest = withdrawalRequests.some(r => r.status === 'pending');
  const canWithdraw = currentEarnings >= minAmount && !hasPendingRequest;

  const getStatusBadge = (status) => {
    switch (status) {
      case 'pending':
        return (
          <Badge className="bg-orange-100 text-orange-700">
            <Clock className="w-3 h-3 mr-1" />
            Pending
          </Badge>
        );
      case 'approved':
        return (
          <Badge className="bg-green-100 text-green-700">
            <CheckCircle className="w-3 h-3 mr-1" />
            Approved
          </Badge>
        );
      case 'denied':
        return (
          <Badge className="bg-red-100 text-red-700">
            <XCircle className="w-3 h-3 mr-1" />
            Denied
          </Badge>
        );
      default:
        return <Badge variant="outline">{status}</Badge>;
    }
  };

  return (
    <>
      <Card className="shadow-lg">
        <CardHeader>
          <CardTitle>Withdrawal</CardTitle>
          <CardDescription>Request withdrawal of your referral earnings</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {/* Available Balance */}
            <div className="bg-gradient-to-br from-green-50 to-emerald-50 p-6 rounded-lg border-2 border-green-200">
              <p className="text-sm text-slate-600 mb-2">Available Balance</p>
              <p className="text-4xl font-bold text-green-600 flex items-center">
                <IndianRupee className="w-8 h-8" />
                {currentEarnings.toFixed(2)}
              </p>
              <p className="text-sm text-slate-600 mt-2">
                Minimum withdrawal: ₹{minAmount}
              </p>
            </div>

            {/* Withdrawal Button */}
            <Button
              className="w-full bg-gradient-to-r from-green-600 to-emerald-500 hover:from-green-700 hover:to-emerald-600"
              disabled={!canWithdraw}
              onClick={() => setDialogOpen(true)}
            >
              <Wallet className="w-4 h-4 mr-2" />
              {hasPendingRequest 
                ? 'Pending Request' 
                : currentEarnings < minAmount 
                ? `Need ₹${(minAmount - currentEarnings).toFixed(2)} more`
                : 'Request Withdrawal'}
            </Button>

            {!canWithdraw && (
              <div className="flex gap-2 items-start bg-blue-50 p-3 rounded-lg border border-blue-200">
                <AlertCircle className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                <div className="text-sm text-blue-700">
                  {hasPendingRequest 
                    ? 'You have a pending withdrawal request. Please wait for admin approval.'
                    : `Earn ₹${(minAmount - currentEarnings).toFixed(2)} more to request withdrawal.`}
                </div>
              </div>
            )}

            {/* Withdrawal History */}
            {withdrawalRequests.length > 0 && (
              <div>
                <h3 className="font-semibold text-slate-900 mb-3">Withdrawal History</h3>
                <div className="space-y-2">
                  {withdrawalRequests.map((request) => (
                    <div key={request.id} className="border rounded-lg p-3 bg-slate-50">
                      <div className="flex justify-between items-start mb-2">
                        <div>
                          <p className="font-semibold text-lg">₹{request.amount.toFixed(2)}</p>
                          <p className="text-sm text-slate-600">
                            {new Date(request.created_at).toLocaleDateString()}
                          </p>
                        </div>
                        {getStatusBadge(request.status)}
                      </div>
                      {request.upi_id && (
                        <p className="text-sm text-slate-600">UPI: {request.upi_id}</p>
                      )}
                      {request.admin_comment && (
                        <div className="mt-2 bg-white p-2 rounded border border-slate-200">
                          <p className="text-xs text-slate-600">Admin Comment:</p>
                          <p className="text-sm italic text-slate-700">{request.admin_comment}</p>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Withdrawal Request Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Request Withdrawal</DialogTitle>
            <DialogDescription>
              Enter the amount you want to withdraw and your UPI ID
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label>Amount (₹)</Label>
              <Input
                type="number"
                placeholder={`Min ₹${minAmount}`}
                value={formData.amount}
                onChange={(e) => setFormData({ ...formData, amount: e.target.value })}
                min={minAmount}
                max={currentEarnings}
                step="0.01"
                required
              />
              <p className="text-xs text-slate-500 mt-1">
                Available: ₹{currentEarnings.toFixed(2)}
              </p>
            </div>
            <div>
              <Label>UPI ID</Label>
              <Input
                type="text"
                placeholder="yourname@upi"
                value={formData.upi_id}
                onChange={(e) => setFormData({ ...formData, upi_id: e.target.value })}
                required
              />
              <p className="text-xs text-slate-500 mt-1">
                Enter your UPI ID for payment transfer
              </p>
            </div>
            <div className="flex gap-2 justify-end">
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={loading} className="bg-green-600 hover:bg-green-700">
                {loading ? 'Submitting...' : 'Submit Request'}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default WithdrawalRequestForm;
